import { useCallback, useEffect, useRef, useState } from "react";

interface UseNavScrollPillResult {
  direction: "up" | "down" | null;
  count: number;
  scrollToItem: () => void;
}

export function useNavScrollPill(
  scrollContainerRef: React.RefObject<HTMLElement | null>
): UseNavScrollPillResult {
  const [state, setState] = useState<{ direction: "up" | "down" | null; count: number }>({
    direction: null,
    count: 0,
  });
  const scrollTargetRef = useRef<Element | null>(null);

  const scrollToItem = useCallback(() => {
    scrollTargetRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const itemStates = new Map<Element, { isAbove: boolean; isBelow: boolean }>();
    let intersectionObserver: IntersectionObserver | null = null;

    const updatePillState = () => {
      let aboveCount = 0;
      let belowCount = 0;
      let firstBelow: Element | null = null;
      let firstAbove: Element | null = null;

      for (const [element, { isAbove, isBelow }] of itemStates) {
        const count = Number(element.getAttribute("data-nav-badge-count")) || 0;
        if (isBelow) {
          belowCount += count;
          firstBelow ??= element;
        }
        if (isAbove) {
          aboveCount += count;
          firstAbove ??= element;
        }
      }

      if (belowCount > 0) {
        scrollTargetRef.current = firstBelow;
        setState({ direction: "down", count: belowCount });
      } else if (aboveCount > 0) {
        scrollTargetRef.current = firstAbove;
        setState({ direction: "up", count: aboveCount });
      } else {
        scrollTargetRef.current = null;
        setState({ direction: null, count: 0 });
      }
    };

    const observeTargets = () => {
      intersectionObserver?.disconnect();
      itemStates.clear();

      const targets = Array.from(container.querySelectorAll<Element>("[data-nav-badge-count]"));

      if (targets.length === 0) {
        setState({ direction: null, count: 0 });
        return;
      }

      intersectionObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const itemMid = entry.boundingClientRect.top + entry.boundingClientRect.height / 2;
            const rootMid = (entry.rootBounds?.top ?? 0) + (entry.rootBounds?.height ?? 0) / 2;
            itemStates.set(entry.target, {
              isAbove: !entry.isIntersecting && itemMid < rootMid,
              isBelow: !entry.isIntersecting && itemMid >= rootMid,
            });
          }
          updatePillState();
        },
        { root: container, threshold: 0 }
      );

      for (const target of targets) {
        itemStates.set(target, { isAbove: false, isBelow: false });
        intersectionObserver.observe(target);
      }
    };

    observeTargets();

    const mutationObserver = new MutationObserver((mutations) => {
      const hasRelevantChange = mutations.some(
        (mutation) =>
          (mutation.type === "attributes" && mutation.attributeName === "data-nav-badge-count") ||
          mutation.type === "childList"
      );
      if (hasRelevantChange) {
        observeTargets();
      }
    });

    mutationObserver.observe(container, {
      attributes: true,
      attributeFilter: ["data-nav-badge-count"],
      childList: true,
      subtree: true,
    });

    return () => {
      intersectionObserver?.disconnect();
      mutationObserver.disconnect();
    };
  }, [scrollContainerRef]);

  return { direction: state.direction, count: state.count, scrollToItem };
}
